import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

export const WaitlistIntro = styled('p')(() => ({
  margin: '0 0 16px',
  color: muted,
  fontSize: '0.95rem',
  lineHeight: 1.5,
  maxWidth: 720,
}));

export const SectionLabel = styled('h3')(() => ({
  margin: '0 0 12px',
  color: ink,
  fontSize: '1.05rem',
  fontWeight: 700,
  letterSpacing: '-0.01em',
}));

export const SectionLabelSpaced = styled(SectionLabel)(() => ({
  marginTop: 32,
}));

export const SectionPanel = styled(Box)(() => ({
  borderRadius: 16,
  border: '1px solid rgba(13, 78, 254, 0.12)',
  background: '#fff',
  boxShadow: '0 8px 28px rgba(13, 78, 254, 0.06)',
  overflow: 'hidden',
}));

export const StatRow = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 24,
}));

export const StatCard = styled(Box)(() => ({
  minWidth: 140,
  padding: '14px 16px',
  borderRadius: 14,
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.16) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.12)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.65)',
}));

export const StatValue = styled('div')(() => ({
  color: ink,
  fontWeight: 800,
  fontSize: '1.45rem',
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
}));

export const StatLabel = styled('div')(() => ({
  color: muted,
  fontSize: '0.78rem',
  fontWeight: 600,
  marginTop: 4,
}));

export const EmptyState = styled(Box)(() => ({
  padding: '28px 20px',
  textAlign: 'center',
  color: muted,
  fontSize: '0.9rem',
  lineHeight: 1.5,
  background:
    'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, #ffffff 100%)',
}));

export const LeadList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
}));

export const LeadCard = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '14px 18px',
  borderBottom: '1px solid rgba(13, 78, 254, 0.08)',
  background: '#fff',
  transition: 'background 0.15s ease',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    background:
      'linear-gradient(135deg, rgba(1, 235, 178, 0.04) 0%, rgba(13, 78, 254, 0.03) 100%)',
  },
  '@media (max-width: 600px)': {
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
}));

export const LeadBody = styled(Box)(() => ({
  flex: '1 1 auto',
  minWidth: 0,
  cursor: 'pointer',
  borderRadius: 10,
  margin: '-6px',
  padding: '6px',
  '&:focus-visible': {
    outline: '2px solid rgba(13, 78, 254, 0.45)',
    outlineOffset: 2,
  },
}));

export const LeadHeader = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '4px 10px',
  marginBottom: 6,
}));

export const LeadTitle = styled('div')(() => ({
  color: ink,
  fontWeight: 700,
  fontSize: '1rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
}));

export const LeadWhen = styled('div')(() => ({
  color: muted,
  fontSize: '0.78rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}));

export const LeadChipRow = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 4,
}));

export const LeadChip = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: blue,
  background:
    'linear-gradient(145deg, rgba(1, 235, 178, 0.18) 0%, rgba(13, 78, 254, 0.12) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.16)',
}));

export const LeadMeta = styled('div')(() => ({
  color: muted,
  fontSize: '0.84rem',
  marginTop: 4,
  lineHeight: 1.5,
}));

export const LeadContact = styled(LeadMeta)(() => ({
  marginTop: 0,
  color: ink,
  fontWeight: 600,
  fontSize: '0.86rem',
}));

export const LeadDetails = styled(Box)(() => ({
  marginTop: 8,
  padding: '8px 10px',
  borderRadius: 10,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  color: muted,
  fontSize: '0.84rem',
  lineHeight: 1.45,
}));

export const AssignButton = styled(Button)(() => ({
  flexShrink: 0,
  alignSelf: 'center',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  padding: '6px 14px',
  minWidth: 88,
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.28)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.93,
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(255, 255, 255, 0.7)',
    boxShadow: 'none',
  },
  '@media (max-width: 600px)': {
    marginLeft: 'auto',
  },
}));

export const StyledWaitlistDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    width: '100%',
  },
}));

export const WaitlistDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  fontWeight: 700,
  fontSize: '1.125rem',
  color: ink,
  '& .waitlist-dialog-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
  },
}));

export const WaitlistDialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const WaitlistDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  background: '#f7fafc',
}));

export const WaitlistDialogActions = styled(DialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2, 3, 2.5),
  gap: theme.spacing(1.25),
  background:
    'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, rgba(240, 244, 248, 0.98) 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const WaitlistField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: '#fff',
  },
}));

export const WaitlistPanel = styled(Box)(() => ({
  padding: 16,
  borderRadius: 12,
  background: '#fff',
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const WaitlistPrimaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: 'linear-gradient(90deg, #01d4a0 0%, #0b45e0 100%)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.7)',
  },
}));

export const WaitlistSecondaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.35)',
  '&:hover': {
    borderColor: blue,
    background: 'rgba(13, 78, 254, 0.04)',
  },
}));

export const HotspotTableWrap = styled(SectionPanel)(() => ({
  overflowX: 'auto',
  marginBottom: 8,
}));

export const HotspotTable = styled('table')(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontSize: '0.9rem',
}));

export const HotspotHeadCell = styled('th')(() => ({
  textAlign: 'left',
  padding: '12px 16px',
  color: ink,
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.12)',
  whiteSpace: 'nowrap',
  '&:last-child': {
    textAlign: 'right',
  },
}));

export const HotspotRow = styled('tr')(() => ({
  transition: 'background 0.12s ease',
  '&:nth-of-type(even)': {
    background: 'rgba(247, 250, 252, 0.85)',
  },
  '&:hover': {
    background:
      'linear-gradient(90deg, rgba(1, 235, 178, 0.08) 0%, rgba(13, 78, 254, 0.05) 100%)',
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
}));

export const HotspotCell = styled('td')(() => ({
  padding: '13px 16px',
  color: ink,
  borderBottom: '1px solid rgba(13, 78, 254, 0.06)',
  verticalAlign: 'middle',
  '&:last-child': {
    textAlign: 'right',
  },
}));

export const HotspotTrade = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 700,
  color: ink,
}));

export const HotspotArea = styled('span')(() => ({
  color: muted,
  fontWeight: 500,
}));

export const SignupCount = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  padding: '4px 10px',
  borderRadius: 999,
  fontWeight: 800,
  fontSize: '0.85rem',
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 2px 8px rgba(13, 78, 254, 0.25)',
}));
